// ControlMatrix — Dual-Control State System
// Pure TypeScript state management. No imports.

export interface ControlParam {
  id: string;           // unique identifier like "camera.angle"
  label: string;        // human-readable name like "Camera Angle"
  category: string;     // "camera" | "particles" | "lighting" | "audio" | "scene" | "shader"
  aiValue: number;      // what the AI wants (0-1 normalized or raw units)
  userValue: number | null;  // null = not overridden
  activeValue: number;  // what's actually used
  min: number;
  max: number;
  step: number;
  lastAIUpdate: number; // timestamp
  lastUserUpdate: number; // timestamp
  isOverridden: boolean;
}

export const CONTROL_CATEGORIES = [
  'camera', 'particles', 'lighting', 'audio', 'scene', 'shader'
] as const;

export type ControlCategory = typeof CONTROL_CATEGORIES[number];

interface PreRegisteredParam {
  id: string;
  label: string;
  category: ControlCategory;
}

const PRE_REGISTERED_PARAMS: PreRegisteredParam[] = [
  // Camera
  { id: 'camera.angle',           label: 'Camera Angle',           category: 'camera' },
  { id: 'camera.zoom',            label: 'Camera Zoom',            category: 'camera' },
  { id: 'camera.rotation.speed',  label: 'Camera Rotation Speed',  category: 'camera' },

  // Particles
  { id: 'particles.count',        label: 'Particle Count',         category: 'particles' },
  { id: 'particles.size',         label: 'Particle Size',          category: 'particles' },
  { id: 'particles.speed',        label: 'Particle Speed',         category: 'particles' },
  { id: 'particles.color.hue',    label: 'Particle Color Hue',     category: 'particles' },

  // Lighting
  { id: 'lighting.ambient',          label: 'Ambient Light',          category: 'lighting' },
  { id: 'lighting.key.intensity',    label: 'Key Light Intensity',    category: 'lighting' },
  { id: 'lighting.rim.intensity',    label: 'Rim Light Intensity',    category: 'lighting' },
  { id: 'lighting.fill.intensity',   label: 'Fill Light Intensity',   category: 'lighting' },
  { id: 'lighting.strobe.speed',     label: 'Strobe Speed',           category: 'lighting' },
  { id: 'lighting.strobe.intensity', label: 'Strobe Intensity',       category: 'lighting' },
  { id: 'lighting.laser.count',      label: 'Laser Count',            category: 'lighting' },
  { id: 'lighting.laser.speed',      label: 'Laser Speed',            category: 'lighting' },

  // Fog
  { id: 'fog.density',   label: 'Fog Density',    category: 'scene' },
  { id: 'fog.color.r',   label: 'Fog Color R',    category: 'scene' },
  { id: 'fog.color.g',   label: 'Fog Color G',    category: 'scene' },
  { id: 'fog.color.b',   label: 'Fog Color B',    category: 'scene' },

  // Scene
  { id: 'scene.transition.speed', label: 'Scene Transition Speed', category: 'scene' },
  { id: 'scene.brightness',       label: 'Scene Brightness',       category: 'scene' },
  { id: 'scene.contrast',         label: 'Scene Contrast',         category: 'scene' },

  // Shader
  { id: 'shader.warp',        label: 'Shader Warp',        category: 'shader' },
  { id: 'shader.complexity',  label: 'Shader Complexity',  category: 'shader' },
  { id: 'shader.scale',       label: 'Shader Scale',       category: 'shader' },

  // Audio
  { id: 'audio.volume',       label: 'Audio Volume',       category: 'audio' },
  { id: 'audio.bass.boost',   label: 'Bass Boost',         category: 'audio' },
  { id: 'audio.treble.boost', label: 'Treble Boost',       category: 'audio' },

  // Bloom
  { id: 'bloom.intensity',    label: 'Bloom Intensity',    category: 'scene' },
];

export class ControlMatrix {
  private params: Map<string, ControlParam> = new Map();
  public onChange: ((param: ControlParam) => void) | null = null;

  constructor() {
    this.registerPredefinedParams();
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  private registerPredefinedParams(): void {
    for (const def of PRE_REGISTERED_PARAMS) {
      const now = Date.now();
      const param: ControlParam = {
        id: def.id,
        label: def.label,
        category: def.category,
        aiValue: 0.5,
        userValue: null,
        activeValue: 0.5,
        min: 0,
        max: 1,
        step: 0.01,
        lastAIUpdate: now,
        lastUserUpdate: now,
        isOverridden: false,
      };
      this.params.set(def.id, param);
    }
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private resolveActive(param: ControlParam): number {
    return param.userValue !== null ? param.userValue : param.aiValue;
  }

  private logStateChange(param: ControlParam, previousActive: number, source: 'AI' | 'User' | 'Revert'): void {
    const marker = '\u001b[36m[Glass Box]\u001b[0m'; // cyan for console
    const action = source === 'AI'
      ? 'AI updated'
      : source === 'User'
        ? 'User touched'
        : 'Reverted';
    const detail = source === 'User'
      ? `\u001b[33m-> AI decoupled\u001b[0m` // yellow
      : '';
    const valueDelta = source !== 'Revert'
      ? ` \u001b[32m${param.activeValue.toFixed(2)}\u001b[0m (was: \u001b[90m${previousActive.toFixed(2)}\u001b[0m)`
      : ` \u001b[32m${param.activeValue.toFixed(2)}\u001b[0m (AI: \u001b[90m${param.aiValue.toFixed(2)}\u001b[0m)`;

    console.log(`${marker} ${action} ${param.label.toUpperCase()} ${detail} -> Active:${valueDelta}`);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Returns all registered parameters with their current AI/user/active values. */
  getAllParams(): ControlParam[] {
    return Array.from(this.params.values());
  }

  /** Returns only the parameters the user has overridden. */
  getOverrides(): ControlParam[] {
    return Array.from(this.params.values()).filter(p => p.isOverridden);
  }

  /** Updates the AI preference for a parameter. Applied immediately unless overridden. */
  setAIValue(id: string, value: number): void {
    const param = this.params.get(id);
    if (!param) {
      console.warn(`ControlMatrix: Unknown parameter "${id}"`);
      return;
    }

    const clamped = this.clamp(value, param.min, param.max);
    const previousActive = param.activeValue;

    param.aiValue = clamped;
    param.lastAIUpdate = Date.now();

    // Only update active if the user has NOT overridden this param
    if (!param.isOverridden) {
      param.activeValue = clamped;
    }

    this.logStateChange(param, previousActive, 'AI');
    this.notify(param);
  }

  /** User takes manual control of this parameter. The AI keeps running but is ignored for this param. */
  setUserOverride(id: string, value: number): void {
    const param = this.params.get(id);
    if (!param) {
      console.warn(`ControlMatrix: Unknown parameter "${id}"`);
      return;
    }

    const clamped = this.clamp(value, param.min, param.max);
    const previousActive = param.activeValue;

    param.userValue = clamped;
    param.activeValue = clamped;
    param.lastUserUpdate = Date.now();
    param.isOverridden = true;

    this.logStateChange(param, previousActive, 'User');
    this.notify(param);
  }

  /** Clears all user overrides, returning full control to the AI. */
  revertAll(): void {
    for (const param of this.params.values()) {
      if (param.isOverridden) {
        const previousActive = param.activeValue;

        param.userValue = null;
        param.activeValue = param.aiValue;
        param.isOverridden = false;

        this.logStateChange(param, previousActive, 'Revert');
        this.notify(param);
      }
    }
    console.log('\u001b[36m[Glass Box]\u001b[0m \u001b[1mAll overrides cleared. AI has full control.\u001b[0m');
  }

  /** Clears a single user override, returning that parameter to AI control. */
  revertOne(id: string): void {
    const param = this.params.get(id);
    if (!param) {
      console.warn(`ControlMatrix: Unknown parameter "${id}"`);
      return;
    }

    if (param.isOverridden) {
      const previousActive = param.activeValue;

      param.userValue = null;
      param.activeValue = param.aiValue;
      param.isOverridden = false;

      this.logStateChange(param, previousActive, 'Revert');
      this.notify(param);
    }
  }

  /** Returns true if the user has overridden this parameter. */
  isOverridden(id: string): boolean {
    const param = this.params.get(id);
    return param ? param.isOverridden : false;
  }

  /** Returns the current effective value (user override if set, otherwise AI value). */
  getActive(id: string): number {
    const param = this.params.get(id);
    if (!param) {
      console.warn(`ControlMatrix: Unknown parameter "${id}"`);
      return 0;
    }
    return this.resolveActive(param);
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private notify(param: ControlParam): void {
    if (this.onChange) {
      try {
        this.onChange(param);
      } catch (err) {
        console.error('ControlMatrix: onChange callback threw', err);
      }
    }
  }
}
